import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Page from '../../../../../page';
import BackButton from '../../../../BackButton';
import {
  getApisSystemInfo,
  getSubscriptionsData,
  seedPendingSubscriptions,
} from '../../../../../../redux/connectedComponent/daabSubscriptions/daabSubscriptions.slice';
import { restoreOptimisticPending } from '../../../../../../redux/connectedComponent/daabSubscriptions/optimisticPendingStorage';
import './ViewLocations.scss';
import {
  SearchInput,
  Tabs,
  Tab,
  TabPanel,
  Button,
  BUTTON_VARIANTS,
  IconRemove,
  IconAdd,
  StatusIndicator,
  STATUS_INDICATOR_VARIANTS,
  Loader,
} from 'cdk-radial';
import WebhookWarningDialog from '../WebhookWarningDialog';
import { LocationTable } from './LocationsTable';
import { setScreen } from '../../../../../../redux/connectedComponent/uiFactory/uiFactory.slice';
import { screens } from '../../../../../config/uiFactory.config';
import { API_TYPE, PAGE_TITLE, RESOURCE_TYPES } from '../../../../../../constants/constants';
import { AMPLITUDE_EVENTS } from '../../../../../../constants/amplitudeConstants';
import {
  getOrgAmplitudeProperties,
  sendAmplitudeData,
} from '../../../../../../utils/amplitude';
import { DUMMY_WEBHOOK_URL } from '../../../../../../constants/APIDirectoryConstants';

const PLACEHOLDER = 'Search locations by name or CMF number';
const SEARCH_INPUT_STYLE = {
  width: '350px',
  justifySelf: 'right',
};

const COLOR_STROKE = '#006CFA';
const LOADER_STYLE = {
  stroke: COLOR_STROKE,
  'margin-top': '200px ',
  display: 'flex',
};

const MY_APIS_PATH = '/dp/my-apis';
const getApiScopeKey = (apiId, apiType) => `${apiId || ''}::${apiType || ''}`;

const getBundleScopedLocations = (allLocations = [], bundleLocations, bundleId) => {
  if (!bundleId || !Array.isArray(bundleLocations) || bundleLocations.length === 0) {
    return bundleId ? [] : allLocations;
  }

  const bundleOrgIds = new Set(
    bundleLocations
      .filter((loc) => loc?.bundleId === bundleId && loc?.orgId)
      .map((loc) => loc.orgId)
  );

  return allLocations.filter((loc) => bundleOrgIds.has(loc.entityId));
};

const toViewLocationRow = (subscription = {}, location = {}, entityId = null) => {
  const resolvedEntityId =
    entityId ||
    subscription?.orgId ||
    subscription?.entityId ||
    subscription?.dataOwnerOrgId ||
    location?.entityId ||
    null;

  return {
    id: resolvedEntityId,
    entityId: resolvedEntityId,
    orgId: resolvedEntityId,
    entityName: location?.orgName || location?.name || '',
    address:
      location?.addresses?.[0]
        ? `${location.addresses[0].streetAddress}, ${location.addresses[0].city}`
        : '',
    storeId: location?.storeId || null,
    cmf: subscription?.cmf || null,
    dms: Array.isArray(subscription?.dms) ? [...subscription.dms] : [],
    status: subscription?.status || null,
    requestedStatus:
      subscription?.requested_status || subscription?.requestedStatus || null,
    optimisticApiScopeKey: subscription?.optimisticApiScopeKey || null,
  };
};

const ACTIVE_STATUSES = ['active', 'enabled', 'subscribed'];
const PENDING_STATUSES = ['pending', 'requested', 'in review', 'in_review'];

const isStatus = (status, allowedStatuses) =>
  allowedStatuses.includes((status || '').toLowerCase());

const getSubscriptionEntityId = (item) =>
  item?.orgId || item?.entityId || item?.dataOwnerOrgId;

const uniqueByEntityId = (items = []) => {
  const mapByEntityId = new Map();

  items.forEach((item) => {
    const entityId = getSubscriptionEntityId(item);
    if (!entityId || mapByEntityId.has(entityId)) {
      return;
    }
    mapByEntityId.set(entityId, item);
  });

  return Array.from(mapByEntityId.values());
};

const buildScopedSubscriptions = ({
  subscriptions,
  currentApiScopeKey,
  hasBundleScope,
  scopedLocationEntityIds,
}) => {
  const allSubscriptions = Array.isArray(subscriptions) ? subscriptions : [];

  return allSubscriptions.filter((item) => {
    if (!currentApiScopeKey || currentApiScopeKey === '::') {
      return true;
    }

    if (item?.optimisticApiScopeKey !== currentApiScopeKey) {
      return false;
    }

    if (!hasBundleScope || scopedLocationEntityIds.size === 0) {
      return true;
    }

    return scopedLocationEntityIds.has(getSubscriptionEntityId(item));
  });
};

const mapSubscriptionsToRows = (items = [], scopedAllLocations = []) =>
  items.map((item) => {
    const subEntityId = getSubscriptionEntityId(item);
    const location = scopedAllLocations.find(
      (locations) => locations.entityId === subEntityId
    );

    return toViewLocationRow(item, location, subEntityId);
  });

const buildInactiveRows = (scopedAllLocations = [], excludedIds = new Set()) => {
  const inactiveLocationSet = Array.from(
    new Map(
      scopedAllLocations
        .filter((obj) => !excludedIds.has(obj.entityId))
        .map((entity) => [entity.entityId, entity])
    ).values()
  );

  return inactiveLocationSet.map((entity) =>
    toViewLocationRow({}, entity, entity.entityId)
  );
};

const ViewLocations = ({ apiData }) => {
  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  const [searchInput, setSearchInput] = useState('');
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [api] = useState(apiData);
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);
  const [pendingSubscriptions, setPendingSubscriptions] = useState([]);
  const [inactiveSubscriptions, setInactiveSubscriptions] = useState([]);
  const [showWebhookWarningDialog, setShowWebhookWarningDialog] = useState(false);
  const { authenticationToken: token, allLocations = [] } = state?.loginDetailsSlice;
  const app = state?.orderAPISlice?.orders?.app;

  const scopedAllLocations = useMemo(
    () =>
      getBundleScopedLocations(
        allLocations,
        apiData?.bundleLocations,
        apiData?.bundleId
      ),
    [allLocations, apiData?.bundleLocations, apiData?.bundleId]
  );

  const currentApiScopeKey = getApiScopeKey(api?.id, api?.type);

  const {
    subscriptions,
    fetchingSubscriptions,
    dmsSupportedApis,
    fetchingSystemInfo,
  } = state.daabSubscriptionsSlice;
  const orgAmplitudeProperties = getOrgAmplitudeProperties(
    state?.loginDetailsSlice
  );

  useEffect(() => {
    const restoredPending = restoreOptimisticPending();
    if (restoredPending.length > 0) {
      dispatch(seedPendingSubscriptions(restoredPending));
    }

    dispatch(
      getSubscriptionsData({
        solutionId: app.id,
        apiId: api.id,
        apiType: api.type,
        token,
      })
    );
    const apis = [{ id: api.id }];
    if (api.type === API_TYPE.ASYNC) apis[0]['apiType'] = api.type;
    dispatch(getApisSystemInfo({ apis, token }));
  }, [api.id, api.type, app.id, dispatch, token]);

  useEffect(() => {
    if (fetchingSubscriptions) {
      return;
    }

    const hasBundleScope = Boolean(apiData?.bundleId);
    const scopedLocationEntityIds = new Set(
      scopedAllLocations.map((location) => location.entityId).filter(Boolean)
    );

    const scopedSubscriptions = buildScopedSubscriptions({
      subscriptions,
      currentApiScopeKey,
      hasBundleScope,
      scopedLocationEntityIds,
    });

    const active = uniqueByEntityId(
      scopedSubscriptions.filter((item) => isStatus(item.status, ACTIVE_STATUSES))
    );

    const activeEntityIds = new Set(
      active.map((item) => getSubscriptionEntityId(item)).filter(Boolean)
    );

    const pending = uniqueByEntityId(
      scopedSubscriptions.filter((item) => {
        const entityId = getSubscriptionEntityId(item);

        if (entityId && activeEntityIds.has(entityId)) {
          return false;
        }

        return isStatus(item.status, PENDING_STATUSES);
      })
    );

    const activeResult = mapSubscriptionsToRows(active, scopedAllLocations);
    const pendingResult = mapSubscriptionsToRows(pending, scopedAllLocations);

    const excludedIds = new Set([
      ...activeEntityIds,
      ...pending.map((item) => getSubscriptionEntityId(item)).filter(Boolean),
    ]);

    const inactiveResult = buildInactiveRows(scopedAllLocations, excludedIds);

    setActiveSubscriptions(activeResult);
    setPendingSubscriptions(pendingResult);
    setInactiveSubscriptions(inactiveResult);
  }, [
    subscriptions,
    fetchingSubscriptions,
    scopedAllLocations,
    currentApiScopeKey,
    apiData?.bundleId,
  ]);

  const handleAddLocation = () => {
    if(apiData?.type === RESOURCE_TYPES.ASYNC_API && (!apiData?.webhookUrl || apiData?.webhookUrl === DUMMY_WEBHOOK_URL)) {
      setShowWebhookWarningDialog(true);
    } else {
      dispatch(
        setScreen({
          screen: screens.DAAB_ADD_LOCATIONS,
          solutionData: app,
          additionalData: {
            ...apiData,
            bundleId: apiData?.bundleId,
            bundleLocations: apiData?.bundleLocations,
          },
          prevScreen: screens.VIEW_LOCATIONS,
        })
      );
    }
    sendAmplitudeData(
      AMPLITUDE_EVENTS.MANAGE_LOCATIONS_ADD_LOCATIONS,
      orgAmplitudeProperties
    );
  };
  const handleRemoveLocation = () => {
    dispatch(
      setScreen({
        screen: screens.DAAB_REMOVE_LOCATIONS,
        solutionData: app,
        additionalData: {
            ...apiData,
            bundleId: apiData?.bundleId,
            bundleLocations: apiData?.bundleLocations,
          },
        prevScreen: screens.VIEW_LOCATIONS,
      })
    );
    sendAmplitudeData(
      AMPLITUDE_EVENTS.MANAGE_LOCATIONS_REMOVE_LOCATIONS,
      orgAmplitudeProperties
    );
  };
  const onBack = () => {
    dispatch(setScreen({ screen: screens.MY_APIS }));
  };

  return (
    <Page pageHeader={PAGE_TITLE.MY_APIS}>
      <div className="main-menu-container">
        <BackButton path={MY_APIS_PATH} onBack={onBack} />
        <div className="view-locations-container">
          <div className="heading">{api.name}</div>
          <div className="sub-heading-grid">
            <div className="sub-heading">
              View the status of the connections between your Dealerships and
              this API.
            </div>
            <SearchInput
              placeholder={PLACEHOLDER}
              isAlwaysExpanded
              style={SEARCH_INPUT_STYLE}
              onChange={(input) => setSearchInput(input)}
              value={searchInput}
            />
          </div>
          <div className="view-locations-tabs">
            <Tabs
              activeItemIndex={activeItemIndex}
              items={[
                {
                  id: 'tab-1',
                  panelId: 'panel-1',
                  renderPanel: (props) => (
                    <TabPanel {...props}>
                      {fetchingSubscriptions || fetchingSystemInfo ? (
                        <Loader
                          style={LOADER_STYLE}
                          label="Fetching Subscriptions..."
                        />
                      ) : (
                        <LocationTable
                          locations={activeSubscriptions}
                          searchInput={searchInput}
                          status={'active'}
                          apiDms={dmsSupportedApis[api.id]}
                          apiData={apiData}
                          app={app}
                        />
                      )}
                    </TabPanel>
                  ),
                  renderTab: (props) => (
                    <Tab
                      label={
                        <StatusIndicator
                          label={`Active (${activeSubscriptions.length})`}
                          showIcon
                          variant={STATUS_INDICATOR_VARIANTS.POSITIVE}
                          className="tab-status-indicator"
                          backgroundColor="transparent"
                          borderColor="transparent"
                        />
                      }
                      hideLabel={false}
                      {...props}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveItemIndex(0);
                      }}
                    />
                  ),
                },
                {
                  id: 'tab-2',
                  panelId: 'panel-2',
                  renderPanel: (props) => (
                    <TabPanel {...props}>
                      {fetchingSubscriptions || fetchingSystemInfo ? (
                        <div className="">
                          <Loader
                            style={LOADER_STYLE}
                            label="Fetching Subscriptions..."
                          />
                        </div>
                      ) : (
                        <LocationTable
                          locations={pendingSubscriptions}
                          searchInput={searchInput}
                          status={'pending'}
                        />
                      )}
                    </TabPanel>
                  ),
                  renderTab: (props) => (
                    <Tab
                      label={
                        <StatusIndicator
                          label={`Pending (${pendingSubscriptions.length})`}
                          showIcon
                          variant={STATUS_INDICATOR_VARIANTS.WARNING}
                          className="tab-status-indicator"
                          backgroundColor="transparent"
                          borderColor="transparent"
                        />
                      }
                      hideLabel={false}
                      {...props}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveItemIndex(1);
                      }}
                    />
                  ),
                },
                {
                  id: 'tab-3',
                  panelId: 'panel-3',
                  renderPanel: (props) => (
                    <TabPanel {...props}>
                      {fetchingSubscriptions || fetchingSystemInfo ? (
                        <div className="">
                          <Loader
                            style={LOADER_STYLE}
                            label="Fetching Subscriptions..."
                          />
                        </div>
                      ) : (
                        <LocationTable
                          locations={inactiveSubscriptions}
                          searchInput={searchInput}
                          status={'inactive'}
                        />
                      )}
                    </TabPanel>
                  ),
                  renderTab: (props) => (
                    <Tab
                      label={
                        <StatusIndicator
                          label={`Inactive (${inactiveSubscriptions.length})`}
                          showIcon
                          variant={STATUS_INDICATOR_VARIANTS.DEFAULT}
                          className="tab-status-indicator"
                        />
                      }
                      hideLabel={false}
                      {...props}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveItemIndex(2);
                      }}
                    />
                  ),
                },
              ]}
              onActiveItemChange={setActiveItemIndex}
            />
          </div>
          <div className="view-location-button-container">
            <Button
              className="view-location-button"
              text="Add Locations"
              icon={<IconAdd height={24} width={24} className="icon-order" />}
              variant={BUTTON_VARIANTS.PRIMARY}
              onClick={handleAddLocation}
            />
            <Button
              className="view-location-button"
              text="Remove Locations"
              icon={
                <IconRemove height={24} width={24} className="icon-order" />
              }
              variant={BUTTON_VARIANTS.SECONDARY}
              onClick={handleRemoveLocation}
            />
          </div>
        </div>
      </div>

      <WebhookWarningDialog
        isOpen={showWebhookWarningDialog}
        onClose={() => setShowWebhookWarningDialog(false)}
      />
    </Page>
  );
};

export default ViewLocations;
